import React, { useState, useMemo, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useTranslation } from "react-i18next";
import { CalendarDays, Copy, Settings, ChevronLeft, ChevronRight, CalendarIcon, Plus, X, Repeat, Calendar as CalendarLucide } from "lucide-react";
import { format, addWeeks, startOfWeek, addDays } from "date-fns";
import { fr, enUS } from "date-fns/locale";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

interface DuplicateScheduleDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onDuplicate: (config: DuplicationConfig) => Promise<void>;
  currentWeekDate: Date;
  selectedCalendarId?: string | null;
}

export interface DuplicationConfig {
  type: 'weekly' | 'daily';
  // Weekly
  sourceWeek: Date;
  numberOfWeeks: number;
  selectedDays: string[];
  targetStartWeek: Date;
  // Daily
  sourceDate?: Date;
  targetDates?: string[];
  // Common
  mode: 'replace' | 'merge';
  overwriteExisting: boolean;
  includeWeekends: boolean;
  calendarId?: string | null;
}

export function DuplicateScheduleDialog({
  isOpen,
  onClose,
  onDuplicate,
  currentWeekDate,
  selectedCalendarId
}: DuplicateScheduleDialogProps) {
  const { t, i18n } = useTranslation(['schedule', 'common']);
  const dateFnsLocale = i18n.language === 'fr' ? fr : enUS;

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);

  // `weekDates[0]` peut être absent (semaine sans jour ouvert) : sans repli,
  // `addWeeks(undefined, 1)` produit une date invalide et `format` lève en
  // plein rendu. Le repli est mémoïsé pour garder une référence stable.
  const currentWeekTime =
    currentWeekDate instanceof Date && !isNaN(currentWeekDate.getTime())
      ? currentWeekDate.getTime()
      : null;
  const safeWeekDate = useMemo(
    () => (currentWeekTime !== null ? new Date(currentWeekTime) : new Date()),
    [currentWeekTime]
  );

  const makeInitialConfig = (): DuplicationConfig => ({
    type: 'weekly',
    sourceWeek: safeWeekDate,
    numberOfWeeks: 4,
    mode: 'replace',
    overwriteExisting: true,
    includeWeekends: false,
    selectedDays: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'],
    targetStartWeek: addWeeks(safeWeekDate, 1),
    sourceDate: safeWeekDate,
    targetDates: [],
    calendarId: selectedCalendarId ?? null
  });

  const [config, setConfig] = useState<DuplicationConfig>(makeInitialConfig);

  // `useState` ne relit son argument qu'au premier montage : la semaine source
  // restait figée sur celle affichée au chargement de la page, et on dupliquait
  // donc une autre semaine que celle regardée. On la resynchronise à chaque
  // ouverture du dialogue (et on repart de l'étape 1).
  useEffect(() => {
    if (isOpen) {
      setConfig(makeInitialConfig());
      setStep(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, safeWeekDate, selectedCalendarId]);

  const dayKeys = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;
  const days = useMemo(
    () => dayKeys.map(key => ({
      key,
      label: t(`days.${key}`, { ns: 'schedule', defaultValue: key })
    })),
    [t, i18n.language]
  );

  const toWeekStart = (d: Date) => startOfWeek(d, { weekStartsOn: 1 });
  const isValidDate = (d: unknown): d is Date => d instanceof Date && !isNaN(d.getTime());
  // date-fns v3 lève `RangeError: Invalid time value` sur une date invalide :
  // en plein rendu cela donne un écran blanc. Toutes les mises en forme de
  // date de ce dialogue passent donc par ces deux gardes.
  const formatWeekDate = (date: Date) =>
    isValidDate(date) ? format(toWeekStart(date), 'dd/MM/yyyy') : '—';
  const formatDateInput = (date: Date) =>
    isValidDate(date) ? format(toWeekStart(date), 'yyyy-MM-dd') : '';
  const safeFormat = (value: Date | string | undefined, pattern: string) => {
    const d = value instanceof Date ? value : value ? new Date(value) : undefined;
    return isValidDate(d) ? format(d, pattern, { locale: dateFnsLocale }) : '—';
  };

  const handleDayToggle = (key: string, checked: boolean) => {
    setConfig(prev => ({
      ...prev,
      selectedDays: checked
        ? [...prev.selectedDays, key]
        : prev.selectedDays.filter(d => d !== key)
    }));
  };

  const incSourceWeek = (delta: number) => {
    setConfig(prev => ({
      ...prev,
      sourceWeek: toWeekStart(addWeeks(prev.sourceWeek, delta))
    }));
  };

  const addTargetDate = (date: Date | undefined) => {
    if (!date) return;
    const dateStr = format(date, 'yyyy-MM-dd');
    setConfig(prev => ({
      ...prev,
      targetDates: prev.targetDates?.includes(dateStr)
        ? prev.targetDates
        : [...(prev.targetDates || []), dateStr].sort()
    }));
  };

  const removeTargetDate = (dateStr: string) => {
    setConfig(prev => ({
      ...prev,
      targetDates: (prev.targetDates || []).filter(d => d !== dateStr)
    }));
  };

  const handleConfirm = async () => {
    try {
      setLoading(true);
      await onDuplicate(config);
      toast.success(t("duplicateSuccess", { ns: 'schedule' }));
      handleClose();
    } catch (error) {
      console.error("Erreur duplication:", error);
      toast.error(t("duplicateError", { ns: 'schedule' }));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    onClose();
  };

  const canProceedStep2 = config.type === 'weekly'
    ? config.selectedDays.length > 0
    : (config.targetDates?.length ?? 0) > 0;

  // ─── STEP 1: Mode Selection ───
  const renderStep1 = () => (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        {t('wizard.chooseMode', { ns: 'schedule' })}
      </p>
      <div className="grid grid-cols-2 gap-3">
        <button
          type="button"
          onClick={() => { setConfig(prev => ({ ...prev, type: 'weekly' })); setStep(2); }}
          className="flex flex-col items-center gap-2 rounded-lg border-2 p-4 hover:border-primary hover:bg-primary/5 transition-colors"
        >
          <Repeat className="h-8 w-8 text-primary" />
          <span className="font-medium text-sm">{t('wizard.weeklyMode', { ns: 'schedule' })}</span>
          <span className="text-xs text-muted-foreground text-center">
            {t('wizard.weeklyModeDesc', { ns: 'schedule' })}
          </span>
        </button>
        <button
          type="button"
          onClick={() => { setConfig(prev => ({ ...prev, type: 'daily' })); setStep(2); }}
          className="flex flex-col items-center gap-2 rounded-lg border-2 p-4 hover:border-primary hover:bg-primary/5 transition-colors"
        >
          <CalendarLucide className="h-8 w-8 text-primary" />
          <span className="font-medium text-sm">{t('wizard.dailyMode', { ns: 'schedule' })}</span>
          <span className="text-xs text-muted-foreground text-center">
            {t('wizard.dailyModeDesc', { ns: 'schedule' })}
          </span>
        </button>
      </div>
    </div>
  );

  // ─── STEP 2a: Weekly Config ───
  const renderWeeklyConfig = () => (
    <div className="space-y-4 text-sm">
      {/* Source week */}
      <div className="space-y-2">
        <Label className="flex items-center gap-2 text-xs">
          <CalendarDays className="h-4 w-4" />
          {t('sourceWeek', { ns: 'schedule' })}
        </Label>
        <div className="grid grid-cols-[auto_1fr_auto] gap-2 items-center">
          <Button type="button" variant="outline" size="icon" className="h-9 w-9" onClick={() => incSourceWeek(-1)}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Input
            type="date"
            className="h-9 text-sm"
            value={formatDateInput(config.sourceWeek)}
            onChange={(e) => {
              // Champ vidé ou date partielle : on ignore, sinon `format` lève.
              const parsed = new Date(e.target.value);
              if (!isValidDate(parsed)) return;
              setConfig(prev => ({ ...prev, sourceWeek: toWeekStart(parsed) }));
            }}
          />
          <Button type="button" variant="outline" size="icon" className="h-9 w-9" onClick={() => incSourceWeek(1)}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        <div className="p-2 bg-muted rounded-lg">
          <p className="text-xs font-medium">
            {t('weekOf', { ns: 'schedule' })} {formatWeekDate(config.sourceWeek)}
          </p>
        </div>
      </div>

      {/* Number of weeks */}
      <div className="space-y-2">
        <Label className="text-xs">{t('numberOfWeeks', { ns: 'schedule' })}</Label>
        <Input
          type="number" min="1" max="52" inputMode="numeric" className="h-9 text-sm"
          value={config.numberOfWeeks}
          onChange={(e) => setConfig(prev => ({
            ...prev, numberOfWeeks: Math.max(1, Math.min(52, parseInt(e.target.value) || 1))
          }))}
        />
      </div>

      {/* Target start week */}
      <div className="space-y-2">
        <Label className="text-xs">{t('targetStartWeek', { ns: 'schedule' })}</Label>
        <Input
          type="date" className="h-9 text-sm"
          value={formatDateInput(config.targetStartWeek)}
          onChange={(e) => {
            const parsed = new Date(e.target.value);
            if (!isValidDate(parsed)) return;
            setConfig(prev => ({ ...prev, targetStartWeek: toWeekStart(parsed) }));
          }}
        />
      </div>

      {/* Duplication mode */}
      <div className="space-y-2">
        <Label className="flex items-center gap-2 text-xs">
          <Settings className="h-4 w-4" />
          {t('duplicationMode', { ns: 'schedule' })}
        </Label>
        <Select value={config.mode} onValueChange={(v: 'replace' | 'merge') => setConfig(prev => ({ ...prev, mode: v }))}>
          <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="replace">{t('replace', { ns: 'schedule' })}</SelectItem>
            <SelectItem value="merge">{t('merge', { ns: 'schedule' })}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Day selection */}
      <div className="space-y-2">
        <Label className="text-xs">{t('selectDays', { ns: 'schedule' })}</Label>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {days.map(({ key, label }) => (
            <button
              key={key} type="button"
              onClick={() => handleDayToggle(key, !config.selectedDays.includes(key))}
              className={`flex items-center gap-2 rounded-md border px-2 py-1.5 text-left text-xs
                ${config.selectedDays.includes(key) ? 'bg-primary/10 border-primary' : 'bg-background'}`}
            >
              <Checkbox id={key} checked={config.selectedDays.includes(key)}
                onCheckedChange={(checked) => handleDayToggle(key, !!checked)} />
              <Label htmlFor={key} className="text-xs">{label}</Label>
            </button>
          ))}
        </div>
      </div>

      {/* Overwrite */}
      <div className="flex items-center space-x-2">
        <Checkbox id="overwrite" checked={config.overwriteExisting}
          onCheckedChange={(checked) => setConfig(prev => ({ ...prev, overwriteExisting: !!checked }))} />
        <Label htmlFor="overwrite" className="text-xs">{t('overwriteExisting', { ns: 'schedule' })}</Label>
      </div>
    </div>
  );

  // ─── STEP 2b: Daily Config ───
  const renderDailyConfig = () => (
    <div className="space-y-4 text-sm">
      {/* Source date */}
      <div className="space-y-2">
        <Label className="flex items-center gap-2 text-xs">
          <CalendarDays className="h-4 w-4" />
          {t('wizard.sourceDate', { ns: 'schedule' })}
        </Label>
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className={cn("w-full justify-start text-left text-sm h-9",
              !config.sourceDate && "text-muted-foreground")}>
              <CalendarIcon className="mr-2 h-4 w-4" />
              {config.sourceDate ? safeFormat(config.sourceDate, 'PPP') : t('selectDate', { ns: 'schedule' })}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar mode="single" selected={config.sourceDate} className="p-3 pointer-events-auto"
              onSelect={(d) => d && setConfig(prev => ({ ...prev, sourceDate: d }))}
              locale={dateFnsLocale} />
          </PopoverContent>
        </Popover>
      </div>

      {/* Target dates */}
      <div className="space-y-2">
        <Label className="flex items-center gap-2 text-xs">
          <CalendarDays className="h-4 w-4" />
          {t('wizard.targetDates', { ns: 'schedule' })}
        </Label>
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="w-full text-xs h-9">
              <Plus className="mr-2 h-3 w-3" />
              {t('wizard.addDate', { ns: 'schedule' })}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar mode="single" className="p-3 pointer-events-auto"
              onSelect={(d) => { addTargetDate(d); }}
              disabled={(d) => config.sourceDate && isValidDate(config.sourceDate) ? format(d, 'yyyy-MM-dd') === format(config.sourceDate, 'yyyy-MM-dd') : false}
              locale={dateFnsLocale} />
          </PopoverContent>
        </Popover>

        {(config.targetDates?.length ?? 0) === 0 ? (
          <p className="text-xs text-muted-foreground italic py-2">
            {t('wizard.noTargetDates', { ns: 'schedule' })}
          </p>
        ) : (
          <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
            {config.targetDates?.map(dateStr => (
              <span key={dateStr} className="inline-flex items-center gap-1 rounded-full bg-primary/10 border border-primary/20 px-2.5 py-1 text-xs">
                {safeFormat(dateStr, 'dd MMM yyyy')}
                <button type="button" onClick={() => removeTargetDate(dateStr)}
                  className="hover:text-destructive transition-colors">
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Mode */}
      <div className="space-y-2">
        <Label className="flex items-center gap-2 text-xs">
          <Settings className="h-4 w-4" />
          {t('duplicationMode', { ns: 'schedule' })}
        </Label>
        <Select value={config.mode} onValueChange={(v: 'replace' | 'merge') => setConfig(prev => ({ ...prev, mode: v }))}>
          <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="replace">{t('replace', { ns: 'schedule' })}</SelectItem>
            <SelectItem value="merge">{t('merge', { ns: 'schedule' })}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Overwrite */}
      <div className="flex items-center space-x-2">
        <Checkbox id="overwrite-daily" checked={config.overwriteExisting}
          onCheckedChange={(checked) => setConfig(prev => ({ ...prev, overwriteExisting: !!checked }))} />
        <Label htmlFor="overwrite-daily" className="text-xs">{t('overwriteExisting', { ns: 'schedule' })}</Label>
      </div>
    </div>
  );

  // ─── STEP 3: Confirmation ───
  const renderConfirmation = () => (
    <div className="space-y-3 text-sm">
      <div className="rounded-lg border p-3 space-y-2">
        <p>
          <span className="font-medium">{t('wizard.mode', { ns: 'schedule' })}:</span>{' '}
          {config.type === 'weekly' ? t('wizard.weeklyMode', { ns: 'schedule' }) : t('wizard.dailyMode', { ns: 'schedule' })}
        </p>

        {config.type === 'weekly' ? (
          <>
            <p><span className="font-medium">{t('sourceWeek', { ns: 'schedule' })}:</span> {formatWeekDate(config.sourceWeek)}</p>
            <p><span className="font-medium">{t('targetStartWeek', { ns: 'schedule' })}:</span> {formatWeekDate(config.targetStartWeek)}</p>
            <p><span className="font-medium">{t('numberOfWeeks', { ns: 'schedule' })}:</span> {config.numberOfWeeks}</p>
            <p>
              <span className="font-medium">{t('selectDays', { ns: 'schedule' })}:</span>{' '}
              {config.selectedDays.map(k => t(`days.${k}`, { ns: 'schedule' })).join(', ')}
            </p>
          </>
        ) : (
          <>
            <p>
              <span className="font-medium">{t('wizard.sourceDate', { ns: 'schedule' })}:</span>{' '}
              {config.sourceDate ? safeFormat(config.sourceDate, 'PPP') : '-'}
            </p>
            <p>
              <span className="font-medium">{t('wizard.willDuplicateTo', { ns: 'schedule' })}:</span>{' '}
              {config.targetDates?.map(d => safeFormat(d, 'dd MMM')).join(', ')}
            </p>
          </>
        )}

        <p>
          <span className="font-medium">{t('duplicationMode', { ns: 'schedule' })}:</span>{' '}
          {config.mode === 'replace' ? t('replace', { ns: 'schedule' }) : t('merge', { ns: 'schedule' })}
        </p>

        {config.overwriteExisting && (
          <p className="text-xs text-muted-foreground">{t('overwriteExisting', { ns: 'schedule' })}</p>
        )}
      </div>
    </div>
  );

  const totalSteps = 3;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="w-[95vw] sm:max-w-md p-4 sm:p-6 max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between text-base">
            <span className="flex items-center gap-2">
              <Copy className="h-4 w-4" />
              {t('duplicateSchedule', { ns: 'schedule' })}
            </span>
            {step > 1 && (
              <span className="text-xs text-muted-foreground font-normal">
                {t('wizard.step', { ns: 'schedule' })} {step} {t('wizard.of', { ns: 'schedule' })} {totalSteps}
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        {step === 1 && renderStep1()}
        {step === 2 && config.type === 'weekly' && renderWeeklyConfig()}
        {step === 2 && config.type === 'daily' && renderDailyConfig()}
        {step === 3 && renderConfirmation()}

        {/* Navigation */}
        {step > 1 && (
          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={() => setStep(s => s - 1)} className="flex-1 h-9 text-sm" disabled={loading}>
              {t('wizard.previous', { ns: 'schedule' })}
            </Button>
            {step === 2 ? (
              <Button onClick={() => setStep(3)} className="flex-1 h-9 text-sm" disabled={!canProceedStep2}>
                {t('wizard.next', { ns: 'schedule' })}
              </Button>
            ) : (
              <Button onClick={handleConfirm} className="flex-1 h-9 text-sm" disabled={loading}>
                {loading ? t('loading', { ns: 'common' }) : t('duplicate', { ns: 'schedule' })}
              </Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
